import { ActionPlan, ActionPlanTarget, ActionPlanTargetComment, ActionPlanType } from "@godalming/api-types";
import { ActionPlanLevel, ActionPlanMap } from "./types";

export const academicSupportLevels: ActionPlanLevel[] = [
    { level: 0, name: 'Academic Support 0', abbreviation: 'AS0', color: 'blue', shade: 1 },
    { level: 1, name: 'Academic Support 1', abbreviation: 'AS1', color: 'yellow', shade: 1 },
    { level: 2, name: 'Academic Support 2', abbreviation: 'AS2', color: 'yellow', shade: 4 },
]

export const misconductLevels: ActionPlanLevel[] = [
    { level: 0, name: 'Code of Conduct 0', abbreviation: 'CC0', color: 'red', shade: 1 },
    { level: 1, name: 'Code of Conduct 1', abbreviation: 'CC1', color: 'red', shade: 2 },
    { level: 2, name: 'Code of Conduct 2', abbreviation: 'CC2', color: 'red', shade: 4 },
    { level: 3, name: 'Code of Conduct 3', abbreviation: 'CC3', color: 'red', shade: 6, whiteText: true },
]

export const sustainableStudyLevels: ActionPlanLevel[] = [
    { level: 0, name: 'Sustainable Study 0', abbreviation: 'SS0', color: 'violet', shade: 1 },
    { level: 1, name: 'Sustainable Study 1', abbreviation: 'SS1', color: 'violet', shade: 2 },
    { level: 2, name: 'Sustainable Study 2', abbreviation: 'SS2', color: 'violet', shade: 4, whiteText: true },
    { level: 3, name: 'Sustainable Study 3', abbreviation: 'SS3', color: 'violet', shade: 6, whiteText: true },
]

export const pastoralSupportLevels: ActionPlanLevel[] = [
    { level: 0, name: 'Pastoral Support 0', abbreviation: 'PS0', color: 'blue', shade: 1 },
    { level: 1, name: 'Pastoral Support 1', abbreviation: 'PS1', color: 'blue', shade: 1 },
]

//Action plans map
export const actionPlanTypesMap: Record<string, ActionPlanMap> = {
    'academic-support': { 
        type: 'academic-support', 
        name: 'Academic Support', 
        color: 'yellow',
        levels: academicSupportLevels,
    },
    'academic-support-plan': { 
        type: 'academic-support', 
        name: 'Academic Support', 
        color: 'yellow',
        levels: academicSupportLevels,
    },
    'misconduct': { 
        type: 'misconduct', 
        name: 'Code of Conduct', 
        color: 'red',
        levels: misconductLevels,
    },
    'sustainable-study': { 
        type: 'sustainable-study', 
        name: 'Sustainable Study', 
        color: 'violet',
        levels: sustainableStudyLevels,
    },
    'pastoral-support': { 
        type: 'pastoral-support', 
        name: 'Pastoral Support', 
        color: 'blue',
        levels: pastoralSupportLevels,
    }
}

export const getConfiguredActionPlanLevels = (type?: ActionPlanType | string | null) => {
    if (!type) {
        return Object.values(actionPlanTypesMap).flatMap((actionPlanType) => actionPlanType.levels);
    }

    const actionPlanType = actionPlanTypesMap[type];
    return actionPlanType?.levels ?? [];
};

export const getMaxActionPlanLevel = (type?: ActionPlanType | string | null) => {
    const levels = getConfiguredActionPlanLevels(type);

    if (levels.length === 0) {
        return null;
    }

    return levels.reduce((maxLevel, level) => Math.max(maxLevel, level.level), levels[0].level);
};

export const getMaxActionPlanLevelForType = (type: ActionPlanType | string | null | undefined) => {
    return getMaxActionPlanLevel(type);
};

export const slugifyActionPlanSubject = (description?: string | null) => {
    if (!description) {
        return null;
    }

    return description
        .trim()
        .toLowerCase()
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

export const getActionPlanSubjectSlug = (actionPlan?: ActionPlan | null) => {
    if (!actionPlan || actionPlan.type !== 'academic-support') {
        return null;
    }

    return slugifyActionPlanSubject(actionPlan.subjectInfo?.description);
};

export const findActionPlanForRoute = (actionPlans: ActionPlan[], type: string, subjectSlug?: string | null) => {
    if (type !== 'academic-support') {
        return actionPlans.find((actionPlan) => actionPlan.type === type) ?? null;
    }

    if (!subjectSlug) {
        return actionPlans.find((actionPlan) => actionPlan.type === 'academic-support') ?? null;
    }

    return (
        actionPlans.find((actionPlan) => (
            actionPlan.type === 'academic-support'
            && slugifyActionPlanSubject(actionPlan.subjectInfo?.description) === subjectSlug
        )) ?? null
    );
};

export const findActionPlanTargetById = (actionPlan: ActionPlan | null | undefined, targetId: string) => {
    if (!actionPlan) {
        return null;
    }

    return actionPlan.targets?.find((target) => target.idStudentActionPlanTarget.toString() === targetId) ?? null;
};

export const getActionPlanRouteParams = (studentNo: string, actionPlan: ActionPlan) => ({
    studentNo,
    type: actionPlan.type,
    subjectSlug: getActionPlanSubjectSlug(actionPlan) ?? undefined,
});

export const getActionPlanPageRoute = (studentNo: string, actionPlan: ActionPlan) => {
    if (actionPlan.type === 'academic-support') {
        const subjectSlug = getActionPlanSubjectSlug(actionPlan);
        if (!subjectSlug) {
            return null;
        }

        return {
            to: '/students/$studentNo/action-plans/academic-support/$subjectSlug',
            params: {
                studentNo,
                subjectSlug,
            },
        } as const;
    }

    return {
        to: '/students/$studentNo/action-plans/$type',
        params: {
            studentNo,
            type: actionPlan.type,
        },
    } as const;
};

export const getActionPlanTargetRoute = (studentNo: string, actionPlan: ActionPlan, targetId: string) => {
    if (actionPlan.type === 'academic-support') {
        const subjectSlug = getActionPlanSubjectSlug(actionPlan);
        if (!subjectSlug) {
            return null;
        }

        return {
            to: '/students/$studentNo/action-plans/academic-support/$subjectSlug/target/$targetId',
            params: {
                studentNo,
                subjectSlug,
                targetId,
            },
        } as const;
    }

    return {
        to: '/students/$studentNo/action-plans/$type/target/$targetId',
        params: {
            studentNo,
            type: actionPlan.type,
            targetId,
        },
    } as const;
};

export function getLevelVariablesFromLevelAndType(level: number | string | null | undefined, type: ActionPlanType | string | null | undefined) {

    if (!type) {
        return null;
    }

    const normalisedLevel = level == null ? 0 : Number(level);

    if (Number.isNaN(normalisedLevel)) {
        return null;
    }

    switch (type) {
        case 'academic-support':
            return academicSupportLevels.find(ap => ap.level === normalisedLevel) || null;
        case 'misconduct':
            return misconductLevels.find(ap => ap.level === normalisedLevel) || null;
        case 'code-of-conduct':
            return misconductLevels.find(ap => ap.level === normalisedLevel) || null;
        case 'sustainable-study':
            return sustainableStudyLevels.find(ap => ap.level === normalisedLevel) || null;
        case 'pastoral-support':
            return pastoralSupportLevels.find(ap => ap.level === normalisedLevel) || null;
        default:
            return null;
    }
}

export const getActionPlanDisplayName = (actionPlan?: ActionPlan) => {
    if (!actionPlan) {
        return 'Unknown Action Plan';
    }

    const actionPlanType = actionPlan.type as string;

    if (actionPlanType === 'academic-support') {
        return actionPlan.subjectInfo?.description || 'Academic Support Plan';
    } else {
        return actionPlanTypesMap[actionPlanType].name;
    }
};

export const getTargetStatusLabel = (target: ActionPlanTarget) => {
    switch (target.targetStatus) {
        case 'Not Met':
            return 'Not met';
        case 'Open':
            return 'Active';
        case 'Completed':
            return 'Achieved';
        case 'Deleted':
            return 'Deleted';
        default:
            return target.targetStatus;
    }
};

export const getActionPlanId = (actionPlan?: ActionPlan) => {
    if (!actionPlan) {
        return 'Unknown Action Plan';
    }

    if (actionPlan.type === 'academic-support') {
        return 'academic-support'+'-'+actionPlan.subjectInfo?.studentCourseId.toString();
    }

    return actionPlan.type
};

export function getActionPlanTargetStatusColor(status: number) {
    switch (status) {
        case 1:
            return 'orange';
        case 2:
            return 'green';
        case 3:
            return 'red';
        case 4:
            return 'dark';
        default:
            return 'gray';
    }
}

export function getActionPlanTargetStatusText(status: number | null) {
    switch (status) {
        case 1:
            return 'Active';
        case 2:
            return 'Achieved';
        case 3:
            return 'Not met';
        case 4:
            return 'Deleted';
        default:
            return 'Unknown';
    }
}

import type { SortingState } from "@tanstack/react-table";
import { SortColumnOption } from "@ui/SortManager";

export const actionPlansTableInitialSorting: SortingState = [
    { id: 'status', desc: false },
    { id: 'dateStudentActionPlanTargetDeadline', desc: false }
];

export const actionPlansTableSortColumns: SortColumnOption[] = [
    { value: 'targetType', label: 'Type', valueType: 'category' },
    { value: 'nameStudentActionPlanStaffTarget', label: 'Created By', valueType: 'person' },
    { value: 'dateStudentActionPlanTargetCreated', label: 'Created', valueType: 'date' },
    { value: 'dateStudentActionPlanTargetDeadline', label: 'Deadline', valueType: 'date' },
    { value: 'status', label: 'Status', valueType: 'status' },
];
